// ============================================
// COSMIC HINDU - Product Detail Page (Live API)
// ============================================

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getCategoryById } from '@data/categories';
import { useCart } from '@context/CartContext';
import { useDiscounts } from '@context/DiscountContext';
import { formatPrice } from '@utils/helpers';
import ProductCard from '@components/product/ProductCard';

const API = import.meta.env.VITE_API_URL || '/api';

export default function ProductPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { items, addItem, updateQty, removeItem } = useCart();
  const { discountedPrice } = useDiscounts();
  const [selectedVariants, setSelectedVariants] = useState({}); // { variantIndex: optionIndex }
  const [activeImage, setActiveImage] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewMsg, setReviewMsg] = useState('');

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Fetch product by ID
  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setProduct(null);
    setRelated([]);
    setSelectedVariants({});
    setActiveImage(0);
    setReviews([]);
    setReviewForm({ rating: 5, comment: '' });
    setReviewMsg('');

    fetch(`${API}/get-products.php?id=${productId}`)
      .then(r => r.json())
      .then(d => {
        if (!d.success || (!d.product && !d.data)) {
          setNotFound(true);
        } else {
          const p = d.product || d.data;
          setProduct(p);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [productId]);

  // Fetch related products once we know the category
  useEffect(() => {
    if (!product?.category_id) return;
    fetch(`${API}/get-products.php?category=${product.category_id}&limit=8`)
      .then(r => r.json())
      .then(d => {
        const items = d.data || d.products || [];
        setRelated(items.filter(p => String(p.id) !== String(productId)).slice(0, 4));
      })
      .catch(() => setRelated([]));
  }, [product?.category_id, productId]);

  // Fetch reviews for this product
  useEffect(() => {
    if (!productId) return;
    fetch(`${API}/get-reviews.php?product_id=${productId}`)
      .then(r => r.json())
      .then(d => { if (d.success) setReviews(d.reviews || []); })
      .catch(() => {});
  }, [productId]);

  // Submit review handler
  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('customer_token');
    if (!token) { setReviewMsg('Please log in to submit a review.'); return; }
    if (!reviewForm.comment.trim()) { setReviewMsg('Please write a comment.'); return; }
    setReviewSubmitting(true);
    setReviewMsg('');
    try {
      const res = await fetch(`${API}/submit-review.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ product_id: productId, rating: reviewForm.rating, comment: reviewForm.comment }),
      });
      const d = await res.json();
      if (d.success) {
        setReviewMsg('Review submitted successfully!');
        setReviewForm({ rating: 5, comment: '' });
        // Refresh reviews
        const r2 = await fetch(`${API}/get-reviews.php?product_id=${productId}`);
        const d2 = await r2.json();
        if (d2.success) setReviews(d2.reviews || []);
      } else {
        setReviewMsg(d.message || 'Failed to submit review.');
      }
    } catch { setReviewMsg('Network error. Please try again.'); }
    setReviewSubmitting(false);
  };

  // Loading state
  if (loading) {
    return (
      <div className="text-center py-20">
        <div className="text-4xl mb-4">🌀</div>
        <div className="text-sm tracking-widest text-white/40" style={{ fontFamily: "'Cinzel',serif" }}>
          Loading product…
        </div>
      </div>
    );
  }

  // Not found state
  if (notFound || !product) {
    return (
      <div className="text-center py-20">
        <div className="text-5xl mb-4">🕉️</div>
        <h2 className="text-2xl text-temple-cream mb-4">Product not found</h2>
        <Link to="/shop" className="btn-primary no-underline">Browse Shop</Link>
      </div>
    );
  }

  const category = getCategoryById(product.category_id || product.categoryId);

  // Compute discounted price (parseFloat to avoid string concatenation)
  const categoryId    = product.category_id || product.categoryId;
  const originalPrice = parseFloat(product.price) || 0;
  const productMrp    = parseFloat(product.mrp) || originalPrice;
  const salePrice     = discountedPrice(originalPrice, categoryId);
  const hasSalePrice  = salePrice < originalPrice;
  const basePrice     = hasSalePrice ? salePrice : originalPrice;
  const baseCompare   = hasSalePrice ? originalPrice : productMrp;

  // Variant: selected variant price REPLACES the base price
  const variants = Array.isArray(product.variants) ? product.variants : [];
  let hasVariantSelected = false;
  let variantPrice = 0;
  Object.entries(selectedVariants).forEach(([vi, oi]) => {
    const opt = variants[vi]?.options?.[oi];
    if (opt?.price) {
      hasVariantSelected = true;
      variantPrice += parseFloat(opt.price) || 0;
    }
  });

  const finalPrice = hasVariantSelected ? variantPrice : basePrice;
  const finalComparePrice = hasVariantSelected ? variantPrice : baseCompare;

  // Build selected variant label for cart
  const selectedVariantLabel = Object.entries(selectedVariants)
    .map(([vi, oi]) => {
      const v = variants[vi];
      const opt = v?.options?.[oi];
      return opt ? `${v.type || 'Option'}: ${opt.label}` : '';
    })
    .filter(Boolean)
    .join(', ');

  // Cart item lookup for this product+variant combo
  const cartKey = `${product.id}${selectedVariantLabel ? '-' + selectedVariantLabel : ''}`;
  const cartItem = items.find(i => (i.cartKey || i.id) === cartKey || (!selectedVariantLabel && String(i.id) === String(product.id)));
  const cartQty = cartItem ? cartItem.qty : 0;

  // Live rating from reviews
  const liveRating = reviews.length > 0
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : (product.rating || 4.5);
  const liveReviewCount = reviews.length || product.reviews || 0;

  const images = Array.isArray(product.images) ? product.images : [];

  return (
    <section className="py-6">
      <div className="max-w-6xl mx-auto px-4">
        {/* Breadcrumb */}
        <div className="text-xs text-white/35 mb-5">
          <Link to="/" className="text-white/35 no-underline hover:text-gold-500">Home</Link>
          <span className="mx-1.5">/</span>
          <Link to="/shop" className="text-white/35 no-underline hover:text-gold-500">Shop</Link>
          {category && (
            <>
              <span className="mx-1.5">/</span>
              <Link to={`/shop/${category.id}`} className="text-white/35 no-underline hover:text-gold-500">{category.name}</Link>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Image Gallery — sticky on desktop */}
          <div className="lg:sticky lg:top-20">
            <div className="relative rounded-lg overflow-hidden border border-gold-500/15 mb-2" style={{ aspectRatio: '1/1', maxHeight: 420 }}>
              {images.length > 0 ? (
                <img
                  src={images[activeImage] || images[0]}
                  alt={product.name}
                  className="w-full h-full object-cover transition-opacity duration-300"
                />
              ) : (
                <div className="w-full h-full bg-gold-500/5 flex items-center justify-center text-5xl">🕉️</div>
              )}
              {product.badge && (
                <span className="badge badge-bestseller absolute top-3 left-3 text-[10px] px-2 py-0.5">{product.badge}</span>
              )}
              {hasSalePrice && (
                <span className="badge bg-emerald-500/90 text-white absolute top-3 left-3 text-[10px] px-2 py-0.5">SALE</span>
              )}
              {images.length > 1 && (
                <>
                  <button
                    onClick={() => setActiveImage(prev => prev === 0 ? images.length - 1 : prev - 1)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(0,0,0,.55)', border: '1px solid rgba(255,184,0,.25)', color: '#FFB800', cursor: 'pointer', fontSize: '.9rem' }}
                  >‹</button>
                  <button
                    onClick={() => setActiveImage(prev => prev === images.length - 1 ? 0 : prev + 1)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(0,0,0,.55)', border: '1px solid rgba(255,184,0,.25)', color: '#FFB800', cursor: 'pointer', fontSize: '.9rem' }}
                  >›</button>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-1.5 overflow-x-auto pb-1">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImage(i)}
                    className="shrink-0 w-14 h-14 rounded overflow-hidden"
                    style={{
                      border: activeImage === i ? '2px solid #FFB800' : '1px solid rgba(255,184,0,.15)',
                      opacity: activeImage === i ? 1 : 0.5,
                      cursor: 'pointer', padding: 0, background: 'none',
                    }}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details — compact layout */}
          <div>
            {category && (
              <span className="text-[10px] tracking-[.2em] uppercase text-orange-500 font-semibold">{category.name}</span>
            )}
            <h1 className="text-xl font-bold text-temple-cream mb-2 leading-snug font-display mt-1">{product.name}</h1>

            <div className="flex items-center gap-1.5 mb-3">
              <span className="text-gold-400 text-xs tracking-wider">
                {'★'.repeat(Math.floor(liveRating))}{'☆'.repeat(5 - Math.floor(liveRating))}
              </span>
              <span className="text-white/40 text-xs font-sans">{liveRating} ({liveReviewCount})</span>
            </div>

            <p className="text-white/50 text-sm leading-relaxed mb-4 line-clamp-3">{product.description}</p>

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-4">
              <span className="text-2xl font-bold text-gold-500 font-display">{formatPrice(finalPrice)}</span>
              {!hasVariantSelected && baseCompare > basePrice && (
                <>
                  <span className="text-sm text-white/30 line-through">{formatPrice(baseCompare)}</span>
                  <span className="text-[10px] bg-emerald-400/15 text-emerald-400 px-2 py-0.5 rounded font-bold font-sans">
                    {Math.round((baseCompare - basePrice) / baseCompare * 100)}% off
                  </span>
                </>
              )}
            </div>

            {/* Variants */}
            {variants.length > 0 && (
              <div className="mb-4">
                {variants.map((variant, vi) => (
                  <div key={vi} className="mb-3">
                    <div className="text-[10px] tracking-[.15em] uppercase text-white/45 font-semibold mb-1.5">
                      {variant.type || 'Options'}
                      {selectedVariants[vi] !== undefined && (
                        <span className="text-gold-400 ml-1 normal-case tracking-normal">
                          — {variant.options[selectedVariants[vi]]?.label}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(variant.options || []).map((opt, oi) => {
                        const isSelected = selectedVariants[vi] === oi;
                        return (
                          <button
                            key={oi}
                            onClick={() => setSelectedVariants(prev => prev[vi] === oi ? (() => { const n = { ...prev }; delete n[vi]; return n; })() : { ...prev, [vi]: oi })}
                            className="px-3 py-1.5 rounded text-xs transition-all duration-150"
                            style={{
                              background: isSelected ? 'rgba(255,184,0,.12)' : 'rgba(255,184,0,.04)',
                              border: isSelected ? '1.5px solid #FFB800' : '1px solid rgba(255,184,0,.2)',
                              color: isSelected ? '#FFE082' : 'rgba(255,224,130,.6)',
                              cursor: 'pointer',
                              fontWeight: isSelected ? 600 : 400,
                            }}
                          >
                            {opt.label}
                            {opt.price ? <span style={{ color: '#FFB800', marginLeft: 4, fontSize: '0.7rem' }}>{formatPrice(opt.price)}</span> : ''}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Actions — compact */}
            {cartQty > 0 ? (
              <div className="flex gap-3 mb-4">
                <div className="flex items-center flex-1 rounded overflow-hidden"
                     style={{ border: '1.5px solid #FFB800', background: 'rgba(255,184,0,.05)' }}>
                  <button
                    onClick={() => cartQty === 1 ? removeItem(cartItem.cartKey || cartItem.id) : updateQty(cartItem.cartKey || cartItem.id, cartQty - 1)}
                    className="px-5 py-2.5 text-lg font-bold"
                    style={{ background: 'rgba(255,107,0,.12)', color: '#FFB800', border: 'none', cursor: 'pointer' }}
                  >−</button>
                  <span className="flex-1 text-center text-lg font-bold font-display" style={{ color: '#FFE082' }}>{cartQty}</span>
                  <button
                    onClick={() => updateQty(cartItem.cartKey || cartItem.id, cartQty + 1)}
                    className="px-5 py-2.5 text-lg font-bold"
                    style={{ background: 'rgba(255,107,0,.12)', color: '#FFB800', border: 'none', cursor: 'pointer' }}
                  >+</button>
                </div>
                <button onClick={() => navigate('/checkout')} className="btn-danger flex-1 text-xs py-2.5">⚡ Buy Now</button>
              </div>
            ) : (
              <div className="flex gap-3 mb-4">
                <button
                  onClick={() => addItem({ ...product, price: finalPrice, selectedVariant: selectedVariantLabel || null, cartKey })}
                  className="btn-primary flex-1 text-xs py-2.5"
                >🛒 Add to Cart</button>
                <button
                  onClick={() => { addItem({ ...product, price: finalPrice, selectedVariant: selectedVariantLabel || null, cartKey }); navigate('/checkout'); }}
                  className="btn-danger flex-1 text-xs py-2.5"
                >⚡ Buy Now</button>
              </div>
            )}

            {/* Product Details / Custom Fields */}
            {(() => {
              const specs = product.specifications;
              const entries = Array.isArray(specs)
                ? specs.filter(f => f.name)
                : specs && typeof specs === 'object' && Object.keys(specs).length > 0
                  ? Object.entries(specs).map(([name, value]) => ({ name, value }))
                  : [];
              if (!entries.length) return null;
              return (
                <div className="border border-gold-500/10 rounded overflow-hidden mb-4">
                  <div className="px-3 py-2 bg-gold-500/5 border-b border-gold-500/10">
                    <h3 className="text-[10px] font-semibold text-gold-500 tracking-widest uppercase">Details</h3>
                  </div>
                  <div className="divide-y divide-gold-500/[0.06]">
                    {entries.map((f, i) => (
                      <div key={i} className="flex px-3 py-1.5">
                        <span className="text-xs text-white/40 w-2/5 shrink-0 capitalize font-sans">{f.name}</span>
                        <span className="text-xs text-temple-cream font-sans">{f.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Trust signals — compact row */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2">
              {[
                { icon: '🚚', text: 'Free shipping ₹999+' },
                { icon: '🔄', text: '7-day returns' },
                { icon: '✅', text: '100% authentic' },
                { icon: '🔒', text: 'Secure payments' },
              ].map((f, i) => (
                <span key={i} className="text-white/40 text-[11px] flex items-center gap-1">
                  <span className="text-xs">{f.icon}</span> {f.text}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-16">
          <h3 className="text-2xl text-temple-cream mb-6 font-display">Customer Reviews ({liveReviewCount})</h3>

          {/* Rating Summary */}
          <div className="flex items-center gap-6 mb-8 p-5 rounded-lg" style={{ background: 'rgba(255,184,0,.04)', border: '1px solid rgba(255,184,0,.12)' }}>
            <div className="text-center">
              <div className="text-4xl font-bold text-gold-500 font-display">{liveRating}</div>
              <div className="text-gold-400 text-sm tracking-wider mt-1">
                {'★'.repeat(Math.floor(liveRating))}{'☆'.repeat(5 - Math.floor(liveRating))}
              </div>
              <div className="text-white/40 text-xs mt-1">{liveReviewCount} reviews</div>
            </div>
            <div className="flex-1">
              {[5, 4, 3, 2, 1].map(star => {
                const count = reviews.filter(r => Math.round(r.rating) === star).length;
                const pct = reviews.length ? (count / reviews.length * 100) : 0;
                return (
                  <div key={star} className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-white/50 w-8">{star} ★</span>
                    <div className="flex-1 h-2 rounded-full" style={{ background: 'rgba(255,184,0,.1)' }}>
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #FF6B00, #FFB800)' }} />
                    </div>
                    <span className="text-xs text-white/40 w-6">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Write Review Form */}
          <div className="mb-8 p-5 rounded-lg" style={{ background: 'rgba(255,184,0,.04)', border: '1px solid rgba(255,184,0,.12)' }}>
            <h4 className="text-sm font-semibold text-gold-500 tracking-widest uppercase mb-4">Write a Review</h4>
            {!localStorage.getItem('customer_token') ? (
              <p className="text-white/50 text-sm">
                Please <Link to="/login" className="text-gold-500 no-underline hover:underline">log in</Link> to write a review.
              </p>
            ) : (
              <form onSubmit={handleReviewSubmit}>
                <div className="mb-4">
                  <div className="text-xs text-white/50 mb-2 uppercase tracking-wider">Your Rating</div>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setReviewForm(f => ({ ...f, rating: star }))}
                        className="text-2xl transition-transform hover:scale-110"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: star <= reviewForm.rating ? '#FFB800' : 'rgba(255,184,0,.25)' }}
                      >★</button>
                    ))}
                  </div>
                </div>
                <div className="mb-4">
                  <textarea
                    value={reviewForm.comment}
                    onChange={e => setReviewForm(f => ({ ...f, comment: e.target.value }))}
                    placeholder="Share your experience with this product…"
                    rows={3}
                    className="w-full p-3 rounded text-sm"
                    style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,184,0,.2)', color: '#FFE082', resize: 'vertical', outline: 'none', fontFamily: "'Crimson Pro', serif" }}
                  />
                </div>
                {reviewMsg && (
                  <div className={`text-sm mb-3 ${reviewMsg.includes('success') ? 'text-emerald-400' : 'text-red-400'}`}>
                    {reviewMsg}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={reviewSubmitting}
                  className="btn-primary py-2.5 px-6 text-xs"
                  style={{ opacity: reviewSubmitting ? 0.6 : 1 }}
                >
                  {reviewSubmitting ? 'Submitting…' : '🙏 Submit Review'}
                </button>
              </form>
            )}
          </div>

          {/* Review List */}
          {reviews.length > 0 ? (
            <div className="space-y-4">
              {reviews.map((rev, i) => (
                <div key={rev.id || i} className="p-4 rounded-lg" style={{ background: 'rgba(255,184,0,.03)', border: '1px solid rgba(255,184,0,.08)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                           style={{ background: 'linear-gradient(135deg, #FF6B00, #FFB800)', color: '#0D0500' }}>
                        {(rev.customer_name || 'U')[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-temple-cream">{rev.customer_name || 'Customer'}</div>
                        <div className="text-gold-400 text-xs">
                          {'★'.repeat(Math.floor(rev.rating))}{'☆'.repeat(5 - Math.floor(rev.rating))}
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-white/30">
                      {rev.created_at ? new Date(rev.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                    </span>
                  </div>
                  <p className="text-sm text-white/60 leading-relaxed" style={{ fontFamily: "'Crimson Pro', serif" }}>
                    {rev.comment}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-white/35 text-sm italic">No reviews yet. Be the first to review this product!</p>
          )}
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-16">
            <h3 className="text-2xl text-temple-cream mb-6 font-display">Related Products</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map((r) => <ProductCard key={r.id} product={r} />)}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
